/**
 * Malteries Franco-Belges — Nogent-sur-Seine and Pithiviers, France.
 *
 * MFB is a French maltster inside the Soufflet group, and Country Malt Group
 * distributes it across North America; its Pilsen, Vienna, Munich, Kiln Amber
 * and Special Aromatic have been in Belgian and farmhouse grists for decades.
 *
 * ## Every row here is `representative`, and that is not a shortcut
 *
 * MFB publishes no product specification anywhere reachable. The old
 * malteriesfrancobelges addresses are gone or redirect to investor pages,
 * `souffletmalt.com` carries prose about the group and no analysis, and the
 * distributor listings that do quote figures are JavaScript rendered behind a
 * bot wall. There is no maker page for an individual malt, so no row here
 * carries a `url` — an assembled address would be a fabrication shaped like a
 * link, and a retailer's page is not the maltster's.
 *
 * So the numbers are ours, not MFB's, and every description says so. Colour,
 * extract, moisture and diastatic power are the **median of what this catalog
 * already carries for the malt type**, which keeps them consistent with the
 * malts a brewer would substitute rather than with a half-remembered figure:
 *
 * - **Pilsen** — the 34 pilsner base-malt rows (Weyermann, Bestmalz,
 *   Dingemans Pilsen MD, Castle Pilsen 2RS and the rest): 80.8 % extract,
 *   4.5 % moisture, 1.8 °L, 76 °Lintner.
 * - **Vienna** — the 16 Vienna rows: 80 %, 4.5 %, 3.8 °L, 66 °Lintner.
 * - **Munich** — the 18 *light* Munich rows only (Munich Type I, BEST Munich,
 *   Munich MD, Viking Munich Light and friends), because MFB's is a light
 *   Munich: 79.5 %, 4.5 %, 7.4 °L, 50 °Lintner.
 * - **Kiln Amber** — the amber malts Crisp, Simpsons and Fawcett publish:
 *   72 %, 2.5 %, 25 °L, no diastatic power, and their 10 % ceiling. Amber
 *   malt is classed `munich-vienna` here the way theirs are.
 * - **Special Aromatic** — the three kilned aromatic rows, Dingemans Aromatic
 *   MD, Castle Château Arôme and Briess Aromatic Munich: 78 %, 4.5 %, 20 °L,
 *   20 °Lintner, capped at 20 % like the Belgian pair.
 *
 * `colorEbc` is derived from the stored °L with this catalog's
 * `EBC = 2.65 × °L − 1.2`, as everywhere else. The rows exist so a brewer can
 * pick an MFB malt by the name on the sack and have sane arithmetic behind it.
 * They are not a substitute for a spec sheet, and if MFB ever publishes one
 * these figures should be replaced wholesale.
 */

import type { Fermentable } from '../../schema/fermentable';

/** Malteries Franco-Belges malts. @see Fermentable */
export const mfbFermentables: readonly Fermentable[] = [
  {
    id: 'mfb-pilsen',
    name: 'Pilsen Malt',
    maker: 'Malteries Franco-Belges',
    origin: 'France',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.8,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    colorEbc: 3.6,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The French continental pilsner malt, and the base a great many Belgian and farmhouse grists are actually built on — softer and a shade breadier than a German pilsner malt, with the enzyme to carry sugar and unmalted grain alongside it. MFB publish no analysis, so the figures here are the median of the pilsner malts this catalog already carries.',
    tags: ['base', 'pilsner', 'lager', 'french', 'belgian'],
    provenance: 'representative',
  },
  {
    id: 'mfb-vienna',
    name: 'Vienna Malt',
    maker: 'Malteries Franco-Belges',
    origin: 'France',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 3.8,
    colorEbc: 8.9,
    diastaticPowerLintner: 66,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned a step past pilsner for a golden, lightly toasted sweetness, and enzymic enough to be the whole grist of a Vienna lager or a Belgian pale. Sits where Weyermann and Bestmalz Vienna sit, with the rounder French malt character. MFB publish no analysis; these are the class-typical figures for a Vienna malt.',
    tags: ['kilned', 'vienna', 'french', 'malty', 'toasted'],
    provenance: 'representative',
  },
  {
    id: 'mfb-munich',
    name: 'Munich Malt',
    maker: 'Malteries Franco-Belges',
    origin: 'France',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79.5,
    moisturePercent: 4.5,
    colorLovibond: 7.4,
    colorEbc: 18.4,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A light Munich — the Type I end of the family rather than the dark one — bringing bread crust and depth without much color, and keeping enough enzyme to convert itself at any proportion of the grist. Comparable to BEST Munich or Dingemans Munich MD. MFB publish no analysis, so these are the median figures for a light Munich.',
    tags: ['kilned', 'munich', 'french', 'malty', 'bready'],
    provenance: 'representative',
  },
  {
    id: 'mfb-kiln-amber',
    name: 'Kiln Amber Malt',
    maker: 'Malteries Franco-Belges',
    origin: 'France',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 72,
    moisturePercent: 2.5,
    colorLovibond: 25,
    colorEbc: 65.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale malt taken hot on the kiln to a dry, biscuit-and-toast character with none of the sweetness of a crystal malt at the same depth — the seasoning that gives a Belgian amber or a bière de garde its dusty depth. Treat it like an English amber malt: a little goes a long way. MFB publish no analysis, so color, extract and the 10 % ceiling are the class-typical amber-malt figures, diastatic power included at zero.',
    tags: ['kilned', 'amber', 'french', 'biscuit', 'toasted', 'dry'],
    provenance: 'representative',
  },
  {
    id: 'mfb-special-aromatic',
    name: 'Special Aromatic Malt',
    maker: 'Malteries Franco-Belges',
    origin: 'France',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 20,
    colorEbc: 51.8,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'High-kilned for aroma rather than color, and used as a seasoning: 5–15 % gives a dubbel, a bière de garde or a dark saison the same concentrated sweet-bread lift that Dingemans Aromatic MD or Château Arôme do. A little enzyme survives, but nobody builds a grist on it. MFB publish no analysis, so the figures are the median of the kilned aromatic malts already in this catalog.',
    tags: ['kilned', 'aromatic', 'french', 'malty', 'bready'],
    provenance: 'representative',
  },
];
